import { Grid } from '@mui/material';
import { parseSecondsToHMSText, parseSecondsToHMText } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ReactElement } from 'react';
import { Bar } from 'react-chartjs-2';
import { useIntl } from 'react-intl';
import { ResponsiveContainer } from 'recharts';
import styled from 'styled-components';
import { DistanceIcon } from '../../../../../../assets/svg/Plan/analytics/route/DistanceIcon';
import StopsIcon from '../../../../../../assets/svg/Plan/analytics/route/StopsIcon';
import { StopTimeIcon } from '../../../../../../assets/svg/Plan/analytics/route/StopTimeIcon';
import { TimeClockIcon } from '../../../../../../assets/svg/Plan/analytics/route/TimeClockIcon';
import { getStopStatusColor } from '../../../../../../constants/Colors';
import { kmToSM } from '../../../../../../helpers/Units';
import { useRetrieveActiveProject } from '../../../../../../hooks/project/useRetrieveActiveProject';
import Text from '../../../../../../react-components/Text';
import { useRetrieveAnalyticsPlanRoutes } from '../../../hooks/analytics/useRetrieveAnalyticsPlanRoutes';
import { AnalyticsElement } from './AnalyticsElement';

const PlanRoute = styled(Grid)`
  display: flex;
  flex-direction: column !important;
  padding: 12px 20px;
  border-radius: 10px;
  background-color: ${RoutalPalette.neutral02};
  flex: 1;
`;

const PlanRouteIcon = styled(Grid)`
  display: flex;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 10px;
  align-items: center;
  border-radius: 10px;
  background-color: ${RoutalPalette.neutral40};
`;

interface PlanRoutesElementProps {
  icon: ReactElement;
  value: number | string;
  subtitleId: string;
}

const PlanRoutesElement = ({ icon, value, subtitleId }: PlanRoutesElementProps) => {
  return (
    <PlanRoute>
      <PlanRouteIcon>{icon}</PlanRouteIcon>
      <Text style={{ textAlign: `center`, color: RoutalPalette.neutral40 }} variant="h5">
        {value}
      </Text>
      <div style={{ minHeight: `4px` }} />
      <Text style={{ textAlign: `center`, color: RoutalPalette.neutral20 }} variant="body" intlId={subtitleId} />
      <VSpacer large />
    </PlanRoute>
  );
};

export const PlanRoutes = () => {
  const intl = useIntl();
  const { data: activeProject } = useRetrieveActiveProject();
  const { totalKM, totalMinutes, totalStops, averageMinutesPerStop, stopReports } = useRetrieveAnalyticsPlanRoutes();

  const charData = [
    {
      label: intl.formatMessage({
        id: `planner.plan_details.analytics.routes.completed`,
        defaultMessage: `Completed`,
      }),
      value: stopReports.completed,
      color: getStopStatusColor(`completed`, `light`),
      labelColor: getStopStatusColor(`completed`, `dark`),
    },
    {
      label: intl.formatMessage({
        id: `planner.plan_details.analytics.routes.canceled`,
        defaultMessage: `Canceled`,
      }),
      value: stopReports.canceled,
      color: getStopStatusColor(`canceled`, `light`),
      labelColor: getStopStatusColor(`canceled`, `dark`),
    },
    {
      label: intl.formatMessage({
        id: `planner.plan_details.analytics.routes.incomplete`,
        defaultMessage: `Incomplete`,
      }),
      value: stopReports.incomplete,
      color: getStopStatusColor(`incomplete`, `light`),
      labelColor: getStopStatusColor(`incomplete`, `dark`),
    },
    {
      label: intl.formatMessage({
        id: `planner.plan_details.analytics.routes.pending`,
        defaultMessage: `Pending`,
      }),
      value: stopReports.pending,
      color: getStopStatusColor(`pending`, `light`),
      labelColor: getStopStatusColor(`pending`, `dark`),
    },
  ];

  return (
    <AnalyticsElement
      header={
        <Grid display="flex" flexDirection="row" alignItems="center">
          <StopsIcon color={RoutalPalette.primary80} style={{ width: `18px`, height: `18px` }} />
          <HSpacer small />
          <Text variant="h6" style={{ fontWeight: `600` }} intlId="planner.plan_details.analytics.routes.title" />
        </Grid>
      }
      headerStyle={{ justifyContent: `space-between` }}
      content={
        <Grid display="flex" flexDirection="row" width="100%">
          <Grid display="flex" flexDirection="column" width="100%">
            <Grid display="flex" flexDirection="row" width="100%">
              <PlanRoutesElement
                icon={<DistanceIcon style={{ width: `16px`, height: `16px` }} />}
                value={`${kmToSM(totalKM, activeProject?.units?.distance || `km`)} ${(
                  activeProject?.units?.distance || `km`
                ).toLowerCase()}`}
                subtitleId={`planner.plan_details.analytics.routes.total_distance`}
              />

              <HSpacer small />

              <PlanRoutesElement
                icon={<StopsIcon style={{ width: `16px`, height: `16px` }} />}
                value={totalStops}
                subtitleId={`planner.plan_details.analytics.routes.total_stops`}
              />
            </Grid>

            <VSpacer small />

            <Grid display="flex" flexDirection="row">
              <PlanRoutesElement
                icon={<TimeClockIcon style={{ width: `16px`, height: `16px` }} />}
                value={parseSecondsToHMText(totalMinutes * 60)}
                subtitleId={`planner.plan_details.analytics.routes.total_time`}
              />

              <HSpacer small />

              <PlanRoutesElement
                icon={<StopTimeIcon style={{ width: `16px`, height: `16px` }} />}
                value={parseSecondsToHMSText(averageMinutesPerStop * 60)}
                subtitleId={`planner.plan_details.analytics.routes.stop_time`}
              />
            </Grid>
          </Grid>

          <HSpacer large />

          <ResponsiveContainer width="100%" height="100%">
            <Bar
              data={{
                labels: charData.map((data) => data.label),
                datasets: [
                  {
                    borderRadius: 10,
                    barPercentage: 1,
                    data: charData.map((data) => data.value),
                    backgroundColor: charData.map((data) => data.color),
                    borderColor: charData.map((data) => data.color),
                    borderWidth: 0,
                  },
                ],
              }}
              plugins={[ChartDataLabels]}
              options={{
                plugins: {
                  datalabels: {
                    align: `end`,
                    anchor: `end`,
                    color: charData.map((data) => data.labelColor),
                    font: {
                      weight: `bold`,
                      size: 18,
                    },
                    padding: {
                      top: -15,
                    },
                  },
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    enabled: false,
                  },
                },
                scales: {
                  y: { display: false },
                  x: {
                    grid: {
                      display: false,
                    },
                    ticks: {
                      color: charData.map((data) => data.labelColor),
                      font: {
                        size: 13,
                      },
                    },
                  },
                },
                layout: {
                  padding: {
                    top: 25,
                    left: 10,
                    right: 10,
                  },
                },
              }}
            />
          </ResponsiveContainer>
        </Grid>
      }
    />
  );
};
